/**
 * Damage des pistes : dameuses, passages, ressenti de la neige.
 *
 * Les dameuses sont des entités publiques (tous les joueurs du monde les
 * voient). Comme les skieurs, elles n'envoient qu'un mouvement : le client
 * calcule leur position à chaque image. Différence : leur mouvement est daté
 * en temps de JEU (secondes d'horloge), car tout le damage se raisonne en
 * heures de nuit et l'échelle de la nuit change quand la dernière rentre.
 *
 * Une piste se dame en deux passages : la moitié gauche puis la moitié droite
 * (gauche et droite vues en descendant). Entrée par le bas, la dameuse monte
 * côté gauche et redescend côté droit ; entrée par le haut, l'inverse.
 */
import { BUILDINGS, type BuildingKind } from './catalog';
import type { PisteTrack } from './pistes';
import type { Difficulty, EntityId } from './state';
import { GROOM_SCALE } from './time';

export const GROOMING = {
  /** Vitesse apparente en damage (m par s de simulation, soit à l'écran à ×1). */
  workSpeed: 16,
  /** Vitesse apparente sur les trajets (m par s de simulation). */
  driveSpeed: 22,
  /** Gazole consommé (L par heure de jeu hors du garage). */
  fuelPerHour: 18,
  /** Salaire d'un conducteur de nuit (€ par heure de jeu hors du garage). */
  driverPerHour: 42,
  /** Fin de poste (minute du matin) : chaque dameuse est rentrée au plus tard à cette heure. */
  endMinute: 6 * 60 + 30,
  /**
   * Damage du petit matin : si la neige fraîche a recouvert le velours (moins de `dawnBelow`),
   * les dameuses ressortent à partir de `dawnMinute` et rentrent avant `dawnEndMinute`.
   */
  dawnMinute: 3 * 60 + 30,
  dawnEndMinute: 7 * 60 + 30,
  dawnBelow: 0.5,
  /** Neige minimale pour damer (cm). */
  minDepth: 15,
  /** Au-dessus de ce velours, une piste prévue n'a presque pas été skiée : elle n'est pas redamée. */
  regroomBelow: 0.9,
  /** Qualité d'une piste fraîchement damée, par neige froide puis par temps doux (neige transformée). */
  groomedQuality: 0.92,
  groomedQualityWarm: 0.78,
  /** Usure du velours par descente, pour une piste de 30 m de large. */
  wearPerRun: 0.004,
  /** Écart latéral des passages (fraction de la demi-largeur). */
  lane: 0.5,
  /** Écart latéral sur une piste empruntée sans damer (fraction de la demi-largeur). */
  transitLane: 0.8,
} as const;

/** Vitesses en mètres par seconde de JEU (le mouvement des dameuses est daté en temps de jeu). */
export const GROOM_WORK_SPEED = GROOMING.workSpeed / GROOM_SCALE;
export const GROOM_DRIVE_SPEED = GROOMING.driveSpeed / GROOM_SCALE;

export type GroomerStatus = 'parked' | 'driving' | 'grooming' | 'returning';

export const GROOMER_STATUS_LABEL: Record<GroomerStatus, string> = {
  parked: 'Au garage',
  driving: 'En route',
  grooming: 'En damage',
  returning: 'Retour au garage',
};

/**
 * Mouvement d'une dameuse, daté en temps de jeu (s) :
 * - au garage  [0, garageId, place]
 * - trajet     [1, t0, dur, ax, az, bx, bz]
 * - sur piste  [2, t0, dur, pisteId, u0, u1, lane, pass]
 *   u : abscisse relative le long du tracé (0 en haut, 1 en bas) ;
 *   lane : écart latéral, fraction de la demi-largeur (positif à droite en descendant) ;
 *   pass : 1 si la dameuse dame, 0 si elle emprunte la piste pour passer.
 */
export type GroomerMotion = [0, EntityId, number] | [1, number, number, number, number, number, number] | [2, number, number, EntityId, number, number, number, number];

export interface Groomer {
  id: EntityId;
  rev: number;
  /** Numéro dans la flotte de la station (« Dameuse 2 »). */
  n: number;
  garageId: EntityId;
  status: GroomerStatus;
  m: GroomerMotion;
  /** Piste en cours (damage, ou trajet vers elle). */
  pisteId: EntityId | null;
  /** Moitiés de la piste en cours déjà damées (0, 1 ou 2). */
  halves: number;
  /** Pistes qui restent au programme de la nuit, piste en cours comprise. */
  plan: EntityId[];
  /** Pistes damées cette nuit. */
  done: EntityId[];
}

/** Bilan du damage d'une nuit (privé au gérant). */
export interface GroomingNight {
  /** Jour dont c'est la soirée. */
  night: number;
  /** Début du damage (horloge de jeu). */
  startedAt: number;
  /** Fin : dernière dameuse rentrée, ou null si le damage est en cours. */
  endedAt: number | null;
  /** Pistes prévues cette nuit. */
  planned: EntityId[];
  /** Pistes damées. */
  groomed: EntityId[];
  /** Pistes prévues mais pas damées (poste trop court, carburant épuisé). */
  missed: EntityId[];
  /** Pistes laissées en bosses (hors du plan de damage). */
  moguls: EntityId[];
  /** Heures de dameuse, gazole (L) et salaires (€) de la nuit. */
  hours: number;
  fuel: number;
  cost: number;
  /** Arrêt anticipé : carburant épuisé. */
  outOfFuel: boolean;
  /** Repasse du petit matin après une chute de neige (ajoutée au bilan de la nuit). */
  dawn?: boolean;
}

export function groomerName(g: { n: number }): string {
  return `Dameuse ${g.n}`;
}

/** Nombre de dameuses abritées par un bâtiment ouvert. */
export function garageCapacity(kind: BuildingKind): number {
  return BUILDINGS[kind].groomers ?? 0;
}

/* ------------------------------------------------------------------ */
/* Garage : places de stationnement                                     */
/* ------------------------------------------------------------------ */

interface Placed {
  x: number;
  z: number;
  rot: number;
}

/** Centres des portes sectionnelles du garage, dans son repère (x local, voir groomer_garage.py). */
const DOORS = [-8.5, -2.5, 3.5];
/** Centre d'une dameuse garée : arrière à 40 cm de la façade (z local = 2), avant vers l'extérieur. */
const PARK_Z = 6.75;

function local(b: Placed, lx: number, lz: number): [number, number] {
  const c = Math.cos(b.rot);
  const s = Math.sin(b.rot);
  return [b.x + lx * c + lz * s, b.z - lx * s + lz * c];
}

/** Place n° k devant le garage : [x, z, cap]. Les dameuses sont garées avant vers l'extérieur. */
export function garageSlot(b: Placed, k: number): [number, number, number] {
  const door = DOORS[k % DOORS.length] as number;
  const row = Math.floor(k / DOORS.length);
  const [x, z] = local(b, door, PARK_Z + row * 10);
  return [x, z, b.rot];
}

/** Point de sortie devant la place n° k (la dameuse y passe en partant et en rentrant). */
export function garageExit(b: Placed, k: number): [number, number] {
  const door = DOORS[k % DOORS.length] as number;
  const row = Math.floor(k / DOORS.length);
  return local(b, door, PARK_Z + row * 10 + 16);
}

/* ------------------------------------------------------------------ */
/* Position à un instant donné                                          */
/* ------------------------------------------------------------------ */

const tmp = new Float64Array(5);

export interface GroomerContext {
  garage(id: EntityId): Placed | undefined;
  track(id: EntityId): PisteTrack | null;
}

/**
 * Position au sol d'une dameuse à l'instant de jeu t. Écrit [x, z, cap] dans
 * out et renvoie faux si la position est inconnue (garage ou piste disparus).
 */
export function groomerPose(m: GroomerMotion, t: number, ctx: GroomerContext, out: Float64Array | number[]): boolean {
  switch (m[0]) {
    case 0: {
      const b = ctx.garage(m[1]);
      if (!b) return false;
      const [x, z, yaw] = garageSlot(b, m[2]);
      out[0] = x;
      out[1] = z;
      out[2] = yaw;
      return true;
    }
    case 1: {
      const [, t0, dur, ax, az, bx, bz] = m;
      const k = dur > 0 ? Math.min(1, Math.max(0, (t - t0) / dur)) : 1;
      out[0] = ax + (bx - ax) * k;
      out[1] = az + (bz - az) * k;
      out[2] = bx === ax && bz === az ? 0 : Math.atan2(bx - ax, bz - az);
      return true;
    }
    case 2: {
      const [, t0, dur, pisteId, u0, u1, lane] = m;
      const track = ctx.track(pisteId);
      if (!track) return false;
      const k = dur > 0 ? Math.min(1, Math.max(0, (t - t0) / dur)) : 1;
      const u = u0 + (u1 - u0) * k;
      const s = u * track.length;
      // Direction lue un peu en retrait des extrémités : le dernier segment d'un tracé peut être nul.
      track.path.sample(Math.min(Math.max(s, 0.5), track.length - 0.5), tmp);
      const dx = tmp[3] as number;
      const dz = tmp[4] as number;
      track.path.sample(s, tmp);
      const lateral = lane * track.width * 0.5;
      // Normale droite de la direction de descente (même convention que les skieurs).
      out[0] = (tmp[0] as number) + dz * lateral;
      out[1] = (tmp[2] as number) - dx * lateral;
      out[2] = u1 >= u0 ? Math.atan2(dx, dz) : Math.atan2(-dx, -dz);
      return true;
    }
    default:
      return false;
  }
}

/** Fin d'un mouvement daté (null au garage). */
export function groomerMotionEnd(m: GroomerMotion): number | null {
  return m[0] === 0 ? null : m[1] + m[2];
}

/**
 * Avancement du damage de la piste en cours, par moitié : fraction damée
 * signée (positive : damée depuis le bas, négative : depuis le haut, ±1 :
 * moitié entière). Sert au rendu du velours qui apparaît derrière la dameuse.
 */
export function groomProgress(g: Groomer, t: number): { pisteId: EntityId; left: number; right: number } | null {
  if (g.pisteId === null || g.status === 'parked') return null;
  let left = g.halves >= 1 ? 1 : 0;
  let right = g.halves >= 2 ? 1 : 0;
  const m = g.m;
  if (m[0] === 2 && m[7] === 1 && m[3] === g.pisteId) {
    const [, t0, dur, , u0, u1, lane] = m;
    const k = dur > 0 ? Math.min(1, Math.max(0, (t - t0) / dur)) : 1;
    const u = u0 + (u1 - u0) * k;
    const f = u1 < u0 ? 1 - u : -u;
    if (lane < 0) left = f;
    else right = f;
  }
  return { pisteId: g.pisteId, left, right };
}

/* ------------------------------------------------------------------ */
/* Durées et ressenti                                                   */
/* ------------------------------------------------------------------ */

/** Durée de damage d'une piste (heures de jeu) : deux passages et le demi-tour. */
export function groomHours(trackLength: number, width: number): number {
  const work = (2 * trackLength) / GROOM_WORK_SPEED;
  const turn = (width * GROOMING.lane) / GROOM_DRIVE_SPEED;
  return (work + turn) / 3600;
}

/** Bosses formées sur une piste skiée et non redamée (0..1). */
export function moguls(p: { quality: number; groomed: number }): number {
  return Math.max(0, Math.min(1, (0.55 - p.quality) / 0.45)) * (1 - p.groomed);
}

/**
 * Ressenti de la neige pour un skieur (0..1), à partir de la qualité : le
 * velours du matin plaît à tous, les bosses gênent les débutants, et les
 * experts recherchent les pistes raides laissées en bosses.
 */
export function snowFeel(skill: number, p: { quality: number; groomed: number; groom: boolean; difficulty: Difficulty }): number {
  let q = p.quality + 0.12 * p.groomed;
  const bumps = moguls(p);
  if (skill === 2 && !p.groom && (p.difficulty === 'noire' || p.difficulty === 'rouge')) q = Math.max(q, 0.72 + 0.25 * bumps);
  else if (skill === 0) q -= 0.3 * bumps;
  return Math.max(0, Math.min(1, q));
}

export type SnowSurface = 'velours' | 'damee' | 'tracee' | 'bosses' | 'poudreuse';

export const SNOW_SURFACE_LABEL: Record<SnowSurface, string> = {
  velours: 'Velours frais',
  damee: 'Damée',
  tracee: 'Tracée',
  bosses: 'Bosselée',
  poudreuse: 'Poudreuse',
};

/** État de surface affiché pour une piste. */
export function snowSurface(p: { quality: number; groomed: number }): SnowSurface {
  if (p.groomed >= 0.85) return 'velours';
  if (moguls(p) >= 0.35) return 'bosses';
  if (p.groomed >= 0.45) return 'damee';
  if (p.quality >= 0.8) return 'poudreuse';
  return 'tracee';
}